import React, {Fragment, FunctionComponent, useMemo} from "react";
import {CollectionDataRecord} from "../../../data/model/instrument-model";
import {
    InstrumentTableRow,
    InstrumentTableRunInfo,
    runInfoFromInstrument
} from "../../../data/services/instrument-table-service";
import {ZmwsCellPlot} from "./ZmwsCellPlot";
import { getMaxZmwsForInstrumentType } from "../../../data/model/instrument-type-model";
import {RunPreviewData} from "../../../data/services/run-preview-service";

interface Props {
    runId: string
    instrument: InstrumentTableRow
    previewData?: RunPreviewData
    plotStyle?: React.CSSProperties
}

export const collectionsWithZmws = (runInfo: InstrumentTableRunInfo) =>
    runInfo.collections.filter(collection => collection.sequencingZmws && collection.sequencingZmws.length > 0)

export const ZmwsPlot: FunctionComponent<Props> = (props: Props) => {

    const {instrument, runId} = props

    const runInfo = runInfoFromInstrument(instrument, runId)

    const plotKey = (collection: CollectionDataRecord) => {
        return `${collection.sampleName}_${Math.random()}`
    }

    const cellsWithZmws = collectionsWithZmws(runInfo)
    const numPlots = cellsWithZmws.length

    // Compute a single y-axis max shared by every well in the run so the plots can be
    // compared at a glance, instead of each plot auto-scaling to its own peak.
    const sharedYAxisMax = useMemo(() => {
        const normalizationMax = getMaxZmwsForInstrumentType(instrument.instrumentType)
        const peakCount = cellsWithZmws.reduce((runMax, collection) => {
            const cellMax = collection.sequencingZmws.reduce((m, v) => (v > m ? v : m), 0)
            return cellMax > runMax ? cellMax : runMax
        }, 0)
        return peakCount > 0 ? peakCount / normalizationMax : undefined
    }, [cellsWithZmws, instrument.instrumentType])

    const plotCell = (collection: CollectionDataRecord) => {
        return (
            <div style={props.plotStyle}>
                <ZmwsCellPlot
                    collection={collection}
                    key={plotKey(collection)}
                    instrumentType={instrument.instrumentType}
                    previews={props.previewData ? props.previewData[collection.uniqueId] : null}
                    yAxisMax={sharedYAxisMax}
                />
            </div>
        )
    }

    return (
        <Fragment>
            <div className="d-flex flex-wrap mb-3">
                {numPlots > 0 && plotCell(cellsWithZmws[0])}
                {numPlots > 1 && plotCell(cellsWithZmws[1])}
                {numPlots > 2 && plotCell(cellsWithZmws[2])}
                {numPlots > 3 && plotCell(cellsWithZmws[3])}
                {numPlots > 4 && plotCell(cellsWithZmws[4])}
                {numPlots > 5 && plotCell(cellsWithZmws[5])}
                {numPlots > 6 && plotCell(cellsWithZmws[6])}
                {numPlots > 7 && plotCell(cellsWithZmws[7])}
            </div>
        </Fragment>
    )
}

